package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.shared.CoverageDto
import dev.lingvo.shared.CoverageState
import dev.lingvo.shared.StorePlatform
import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.ScreenshotPlanDto
import dev.lingvo.shared.SlotInput
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LEllipsisIcon
import dev.lingvo.site.ds.LGripVerticalIcon
import dev.lingvo.site.ds.LIconAction
import dev.lingvo.site.ds.LMenu
import dev.lingvo.site.ds.LMenuItem
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LReorderList
import dev.lingvo.site.ds.LSectionHeading
import dev.lingvo.site.ds.LTextArea
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.LToggleChip
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

/** Slot editing shares one draft so autosave, keyboard reorder and undo cannot overwrite each other. */
@Composable
fun ShotSlotPlan(
    slug: String, plan: ScreenshotPlanDto, slots: List<SlotInput>, coverage: CoverageDto?, canEdit: Boolean,
    onEdit: (Int, SlotInput) -> Unit, onMove: (Int, Int) -> Unit, onDuplicate: (Int) -> Unit,
    onDelete: (Int) -> Unit, onRetake: (String) -> Unit,
) {
    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
        LSectionHeading(t("app.projects.slug.screenshots.slot_plan_list.shot_plan", "Shot plan"), t("app.projects.slug.screenshots.slot_plan_list.name_each_screen_and_say_how", "Name each screen and say how to capture it."))
        if (plan.platform == StorePlatform.GOOGLE_PLAY) FeatureGraphicNotice()
        if (slots.size < 2) LAlert(t("app.projects.slug.screenshots.slot_plan_list.plan_at_least_two_shots_to", "Plan at least two shots to tell your app's story."), variant = LAlertVariant.WARNING)
        if (slots.size >= shotPlanLimit(plan.platform)) LAlert(
            tf("screenshots.plan.limit", "{store} shows at most {count} screenshots. Delete one to add another.",
                "store" to plan.platform.displayName, "count" to shotPlanLimit(plan.platform)), variant = LAlertVariant.INFO)
        if (canEdit) {
            LReorderList(slots, key = { it.key }, onMove = onMove, label = t("app.projects.slug.screenshots.slot_plan_list.shot_order", "Shot order"), itemLabel = { it.title }) { slot, grip ->
                ShotSlotRow(slug, plan, slot, slots.indexOfFirst { it.key == slot.key }, coverage, true, grip,
                    onEdit, onDuplicate, onDelete, onRetake)
            }
        } else slots.forEachIndexed { index, slot ->
            ShotSlotRow(slug, plan, slot, index, coverage, false, Modifier, onEdit, onDuplicate, onDelete, onRetake)
        }
    }
}

@Composable
private fun ShotSlotRow(
    slug: String, plan: ScreenshotPlanDto, slot: SlotInput, index: Int, coverage: CoverageDto?, editable: Boolean,
    grip: Modifier, onEdit: (Int, SlotInput) -> Unit, onDuplicate: (Int) -> Unit, onDelete: (Int) -> Unit,
    onRetake: (String) -> Unit,
) {
    val saved = plan.slots.firstOrNull { it.key == slot.key }
    val cells = coverage?.rows?.firstOrNull { it.slotId == saved?.id }?.cells.orEmpty()
    var menu by remember(slot.key) { mutableStateOf(false) }
    Column(Modifier.fillMaxWidth().gap(Space.SM).styleModifier {
        property("padding-block", Space.MD.toString()); property("min-width", "0")
    }) {
        Row(Modifier.fillMaxWidth().gap(Space.SM).styleModifier { property("align-items", "start") }) {
            if (editable) LIconAction(tf("screenshots.plan.reorder", "Reorder {slot}", "slot" to slot.title), {}, modifier = grip) { LGripVerticalIcon() }
            Div(Modifier.styleModifier { property("min-width", "0"); property("flex", "1") }.toAttrs { attr("data-shot-title", slot.key) }) {
                if (editable) LTextField(slot.title, { onEdit(index, slot.copy(title = it.take(200))) }, label =
                    tf("screenshots.plan.title", "Shot {position} title", "position" to index + 1), modifier = Modifier.fillMaxWidth())
                else Text(tf("screenshots.plan.read_title", "{position}. {title}", "position" to index + 1, "title" to slot.title))
            }
            if (editable) LMenu(menu, listOf(
                LMenuItem(t("app.projects.slug.screenshots.slot_plan_list.duplicate", "Duplicate"), disabled = plan.slots.size >= shotPlanLimit(plan.platform)),
                LMenuItem(t("app.projects.slug.screenshots.slot_plan_list.queue_every_language_for_retake", "Queue every language for retake"), disabled = saved == null),
                LMenuItem(t("app.projects.slug.screenshots.slot_plan_list.delete", "Delete"), danger = true),
            ), { menu = it }, { selected ->
                menu = false
                when (selected) { 0 -> onDuplicate(index); 1 -> saved?.let { onRetake(it.id) }; 2 -> onDelete(index) }
            }) { toggle, trigger ->
                LIconAction(tf("screenshots.plan.more", "More actions for {slot}", "slot" to slot.title), toggle, modifier = trigger) { LEllipsisIcon() }
            }
        }
        if (editable) {
            LTextArea(slot.howTo, { onEdit(index, slot.copy(howTo = it.take(4000))) },
                label = tf("screenshots.plan.how_to", "How to capture {slot}", "slot" to slot.title),
                placeholder = t("app.projects.slug.screenshots.slot_plan_list.home_tab_demo_data_loaded_dark", "Home tab, demo data loaded, dark mode off"), modifier = Modifier.fillMaxWidth())
            Row(Modifier.gap(Space.XS).styleModifier { property("flex-wrap", "wrap") }) {
                DeviceClass.forPlatform(plan.platform).forEach { device ->
                    LToggleChip(screenshotDeviceLabel(device), device in slot.devices, { selected ->
                        val devices = if (selected) slot.devices + device else slot.devices - device
                        if (devices.isNotEmpty()) onEdit(index, slot.copy(devices = devices))
                    })
                }
                LToggleChip(t("app.projects.slug.screenshots.slot_plan_list.capture_every_language", "Capture every language"), slot.localizedUi,
                    { onEdit(index, slot.copy(localizedUi = it)) })
            }
        } else if (slot.howTo.isNotBlank()) LMuted(slot.howTo)
        // Display declared fixture intent beside the how-to without claiming app-side seeding.
        plan.demoDataProfiles.firstOrNull { it.key == slot.demoDataProfile }?.let { profile ->
            Div(Modifier.styleModifier { property("overflow-wrap", "anywhere"); property("min-width", "0") }.toAttrs {
                attr("data-shot-demo", slot.key)
            }) {
                LMuted(tf("screenshots.plan.demo_profile", "Synthetic profile: {title} ({key}). Seed: {seed}. Record count: {count}.",
                    "title" to profile.title, "key" to profile.key, "seed" to profile.seed, "count" to profile.recordCount))
            }
        }
        A(href = "/app/projects/${encodeURIComponent(slug)}/keys?search=${encodeURIComponent(slot.key)}", attrs = Modifier.monoFont().styleModifier { property("display", "inline-flex"); property("min-height", "40px"); property("align-items", "center"); property("overflow-wrap", "anywhere"); property("max-width", "100%") }.toAttrs {}) {
            Text("store/${plan.platform.id}/captions/${slot.key}")
        }
        LMuted(tf("screenshots.plan.current_count", "{current} of {total} current", "current" to cells.count { it.state == CoverageState.CURRENT }, "total" to cells.size))
    }
}

private external fun encodeURIComponent(value: String): String
